import { useState, useEffect } from 'react'
import type { NewsItem } from '../../types/finance'
import { getNews } from '../../lib/news-api'

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

function sourceColor(source: string): string {
  const s = source.toLowerCase()
  if (s === 'bloomberg') return 'text-purple-600 dark:text-purple-400'
  if (s === 'reuters') return 'text-orange-600 dark:text-orange-400'
  if (s === 'cnbc') return 'text-blue-600 dark:text-blue-400'
  if (s === 'wall street journal' || s === 'wsj') return 'text-amber-600 dark:text-amber-400'
  if (s === 'x') return 'text-slate-600 dark:text-slate-300'
  return 'text-slate-500 dark:text-slate-400'
}

export default function NewsFeed({ symbol }: { symbol: string }) {
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'articles' | 'tweets'>('all')

  useEffect(() => {
    setLoading(true)
    getNews(symbol)
      .then(setNews)
      .catch(() => setNews([]))
      .finally(() => setLoading(false))
  }, [symbol])

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 animate-pulse">
        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-4" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="mb-3">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
          </div>
        ))}
      </div>
    )
  }

  if (news.length === 0) return null

  const filtered = news.filter((item) => {
    if (filter === 'articles') return item.type === 'article'
    if (filter === 'tweets') return item.type === 'tweet'
    return true
  })

  const articleCount = news.filter((n) => n.type === 'article').length
  const tweetCount = news.filter((n) => n.type === 'tweet').length

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <h3 className="text-slate-900 dark:text-white font-semibold text-sm">Recent News & Social</h3>
        </div>
        <div className="flex bg-slate-200/60 dark:bg-slate-700/60 rounded-lg p-0.5">
          <button
            onClick={() => setFilter('all')}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === 'all' ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            All ({news.length})
          </button>
          <button
            onClick={() => setFilter('articles')}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === 'articles' ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Articles ({articleCount})
          </button>
          <button
            onClick={() => setFilter('tweets')}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              filter === 'tweets' ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            X ({tweetCount})
          </button>
        </div>
      </div>

      {/* News list */}
      <div className="divide-y divide-slate-200/50 dark:divide-slate-700/50 max-h-96 overflow-y-auto">
        {filtered.map((item, i) => (
          <a
            key={i}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-5 py-3 hover:bg-slate-200/30 dark:hover:bg-slate-700/30 transition-colors"
          >
            {item.type === 'tweet' ? (
              <TweetItem item={item} />
            ) : (
              <ArticleItem item={item} />
            )}
          </a>
        ))}
      </div>
    </div>
  )
}

function ArticleItem({ item }: { item: NewsItem }) {
  return (
    <div>
      <div className="flex items-start gap-2 mb-1">
        <span className={`text-xs font-medium ${sourceColor(item.source)} flex-shrink-0 mt-0.5`}>{item.source}</span>
        <h4 className="text-sm text-slate-900 dark:text-white leading-snug line-clamp-2">{item.title}</h4>
      </div>
      {item.summary && (
        <p className="text-slate-500 text-xs leading-relaxed line-clamp-2 ml-0 mt-1">{item.summary}</p>
      )}
      <span className="text-slate-400 dark:text-slate-600 text-xs mt-1 block">{timeAgo(item.publishedAt)}</span>
    </div>
  )
}

function TweetItem({ item }: { item: NewsItem }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        {/* X logo */}
        <svg className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        <span className="text-slate-500 dark:text-slate-400 text-xs font-medium">{item.author}</span>
        {item.isHighEngagement && (
          <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[10px] font-semibold rounded-full flex items-center gap-1">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
            </svg>
            TRENDING
          </span>
        )}
      </div>
      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug line-clamp-3">{item.title}</p>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1 text-slate-500 text-xs">
          {/* Retweet icon */}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span className={item.isHighEngagement ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>{item.retweets?.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-500 text-xs">
          {/* Heart icon */}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span>{item.likes?.toLocaleString()}</span>
        </div>
        <span className="text-slate-400 dark:text-slate-600 text-xs">{timeAgo(item.publishedAt)}</span>
      </div>
    </div>
  )
}
